import { BorderBox } from "@/components/atoms/BorderBox";
import { MarkSearchedPhrase } from "@/components/atoms/MarkSearchedPhrase";
import { Button } from "@/components/ui/button";
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { ArrowRight, Loader } from "lucide-react";
import { ReactNode } from "react";
import { HoverPopover } from "../../../../components/atoms/HoverPopover";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { Link, useParams } from "react-router-dom";
import { TooltipButton } from "@/components/atoms/TooltipButton";

interface SurveyRowProps<S extends Tables<'survey'> = Tables<'survey'>> {
    survey: S;
    crmSystem?: CrmSystemIds | null;
    searchTerm: string;
    isLoading?: boolean;
    children: ReactNode;
    additionalActions?: (s:S) => ReactNode;
}

export function SurveyRow({
    survey,
    crmSystem,
    searchTerm,
    isLoading = false,
    children,
    additionalActions
}: SurveyRowProps) {
    const crmSystemConfig = crmRegistry.getSystemById(crmSystem);
    const { orgId } = useParams();

    if(!survey) return null;

    return (
        <BorderBox
            className="flex-col gap-2 border-l-4"
            style={{ borderLeftColor: crmSystemConfig?.branding.color }}
            size='sm'
        >
            <div className="flex items-center w-full gap-2">
                <div className="flex items-center gap-2">
                    <MarkSearchedPhrase
                        text={survey.name ?? ''}
                        searchTerm={searchTerm}
                        className="text-base font-medium whitespace-nowrap"
                    />
                    {isLoading && <Loader className="animate-spin" size={18} />}
                </div>
                {survey.description && (
                    <HoverPopover
                        onHover
                        content={survey.description}
                    >
                        <MarkSearchedPhrase
                            text={survey.description}
                            searchTerm={searchTerm}
                            className="text-base line-clamp-1 text-muted-foreground"
                        />
                    </HoverPopover>
                )}
                <div className="flex gap-2 ml-auto">
                    {additionalActions && additionalActions(survey)}

                    <TooltipButton
                        size='action'
                        variant='secondary'
                        tooltipContent="Open Project"
                        asChild
                    >
                        <Link to={`/organization/${orgId}/project/${survey.id}/manage-project`}>
                            <ArrowRight size={16} />
                        </Link>
                    </TooltipButton>
                </div>
            </div>
            {children}
        </BorderBox>
    );
}